/**
 * Blog upsell: matcht titel-/tag-keywords tegen best-verkochte collectie via /collections/{handle}/products.json.
 */
(function () {
  /** @param {string} [root] Shopify routes.root_url */
  function normalizeRoot(root) {
    const r = String(root || '').trim();
    if (!r || r === '/') return '';
    return r.replace(/\/$/, '');
  }

  /** @param {string} root @param {string} handle @param {number} page */
  function catalogUrl(root, handle, page) {
    const p = normalizeRoot(root);
    const prefix = p ? `${p}/` : '/';
    const collection = encodeURIComponent(handle || 'all');
    return `${prefix}collections/${collection}/products.json?sort_by=best-selling&limit=250&page=${page}`;
  }

  /** @param {string} root @param {string} handle */
  function productPath(root, handle) {
    const p = normalizeRoot(root);
    const prefix = p ? `${p}/` : '/';
    return `${prefix}products/${encodeURIComponent(handle)}`;
  }

  const GENERIC_KW = new Set([
    'zaaien',
    'zaden',
    'zaad',
    'bio',
    'mix',
    'stappen',
    'plant',
    'plan',
    'tuin',
    'moestuin',
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december',
    'verwachten',
    'verwacht',
    'verwachte',
    'juniregen',
    'junireg',
    'oogst',
    'verwijder',
    'houd',
    'regen',
  ]);

  /** Hele woord in producttitel (sterk genoeg om te stoppen met meer pagina's laden). */
  const SCORE_STRONG = 100;
  /** Na eerste batch: stop bij substring-match op langere termen (60 + lengte). */
  const SCORE_SOFT_FIRST_BATCH = 66;
  /** Minimaal om als zaadtype-match te tellen. */
  const SCORE_MIN_MATCH = 52;

  function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /** @param {string} kw */
  function keywordVariants(kw) {
    const variants = [kw];
    if (kw.length >= 5 && kw.endsWith('en')) {
      const stem = kw.slice(0, -2);
      if (stem.length >= 3) variants.push(stem);
    }
    return variants;
  }

  /**
   * @param {string} titleLc
   * @param {string} kw
   */
  function scoreKeyword(titleLc, kw) {
    if (!kw || kw.length < 3 || GENERIC_KW.has(kw)) return 0;

    let best = 0;
    for (const variant of keywordVariants(kw)) {
      if (!titleLc.includes(variant)) continue;
      try {
        const re = new RegExp(`\\b${escapeRegExp(variant)}\\b`, 'i');
        if (re.test(titleLc)) {
          best = Math.max(best, 100 + variant.length * 2);
          continue;
        }
      } catch (e) {
        /* ignore */
      }
      if (variant.length >= 4) best = Math.max(best, 60 + variant.length);
    }
    return best;
  }

  /**
   * @param {unknown[]} products
   * @param {string[]} keywords
   */
  function pickBestWithScore(products, keywords) {
    let best = null;
    let bestScore = 0;
    let bestIndex = Infinity;

    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      if (!p || typeof p !== 'object') continue;
      const raw = /** @type {{ title?: string }} */ (p);
      const titleLc = String(raw.title || '').toLowerCase();
      let productScore = 0;
      for (const kw of keywords) {
        const sc = scoreKeyword(titleLc, kw.toLowerCase());
        if (sc > productScore) productScore = sc;
      }
      if (productScore >= SCORE_MIN_MATCH && i < bestIndex) {
        bestIndex = i;
        bestScore = productScore;
        best = p;
      }
    }

    return { best: /** @type {Record<string, unknown>|null} */ (best), bestScore };
  }

  /**
   * Eerste match in best-verkochte volgorde voor gegeven keywords.
   * @param {unknown[]} products
   * @param {string[]} keywords
   */
  function pickBestForKeywords(products, keywords) {
    if (!products.length || !keywords.length) return null;
    return pickBestWithScore(products, keywords).best;
  }

  /**
   * Titel-keywords eerst, daarna body-keywords (bullet-lijsten), anders #1 bestseller.
   * @param {unknown[]} products
   * @param {string[]} titleKeywords
   * @param {string[]} bodyKeywords
   */
  function pickBestProduct(products, titleKeywords, bodyKeywords) {
    if (!products.length) return null;

    const titlePick = pickBestForKeywords(products, titleKeywords);
    if (titlePick) return titlePick;

    const bodyPick = pickBestForKeywords(products, bodyKeywords);
    if (bodyPick) return bodyPick;

    if (!titleKeywords.length && !bodyKeywords.length) {
      return /** @type {Record<string, unknown>} */ (products[0]);
    }

    return null;
  }

  /** @param {Record<string, unknown>} product */
  function firstAvailableVariant(product) {
    const vs = /** @type {unknown[]} */ (product.variants || []);
    for (const v of vs) {
      if (v && typeof v === 'object' && (/** @type {{ available?: boolean }} */ (v)).available !== false) {
        return v;
      }
    }
    return vs[0] || null;
  }

  /**
   * @param {string} root
   * @param {string} handle
   * @param {number} page
   */
  async function fetchPage(root, handle, page) {
    const res = await fetch(catalogUrl(root, handle, page), { credentials: 'same-origin' });
    if (!res.ok) return [];
    const data = /** @type {{ products?: unknown[] }} */ (await res.json());
    return data.products || [];
  }

  /**
   * @param {string} root
   * @param {string} handle
   * @param {string[]} keywords
   * @returns {Promise<Record<string, unknown>[]>}
   */
  async function loadCatalogForKeywords(root, handle, titleKeywords, bodyKeywords) {
    const cacheKey = `${handle || 'all'}::best-selling`;
    const searchKeywords = [...titleKeywords, ...bodyKeywords];
    if (
      window.__akBlogUpsellCatalogKey === cacheKey &&
      window.__akBlogUpsellCatalog &&
      Array.isArray(window.__akBlogUpsellCatalog)
    ) {
      return window.__akBlogUpsellCatalog;
    }

    const all = [];
    const BATCH = 6;
    const maxPages = 40;
    let firstBatchDone = false;

    for (let start = 1; start <= maxPages; start += BATCH) {
      const pageNums = [];
      for (let i = 0; i < BATCH && start + i <= maxPages; i++) {
        pageNums.push(start + i);
      }
      const batches = await Promise.all(pageNums.map((p) => fetchPage(root, handle, p)));

      let added = false;
      let catalogEnded = false;
      for (const batch of batches) {
        if (batch.length) {
          added = true;
          all.push(...batch);
          if (batch.length < 250) catalogEnded = true;
        }
      }

      if (!added) break;

      if (searchKeywords.length) {
        const titleScore = titleKeywords.length
          ? pickBestWithScore(all, titleKeywords).bestScore
          : 0;
        const bodyScore =
          titleScore < SCORE_MIN_MATCH && bodyKeywords.length
            ? pickBestWithScore(all, bodyKeywords).bestScore
            : 0;
        const bestScore = Math.max(titleScore, bodyScore);
        if (bestScore >= SCORE_STRONG) break;
        if (!firstBatchDone && bestScore >= SCORE_SOFT_FIRST_BATCH) break;
      } else if (all.length) {
        break;
      }

      firstBatchDone = true;
      if (catalogEnded) break;
    }

    window.__akBlogUpsellCatalogKey = cacheKey;
    window.__akBlogUpsellCatalog = all;
    return /** @type {Record<string, unknown>[]> */ (all);
  }

  function formatEur(decimalStr) {
    const n = parseFloat(String(decimalStr || '').replace(',', '.'));
    if (Number.isNaN(n)) return '';
    return n.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
  }

  function stripHtmlToText(html) {
    const d = document.createElement('div');
    d.innerHTML = String(html || '');
    let t = (d.textContent || '').replace(/\s+/g, ' ').trim();
    if (t.length > 140) t = `${t.slice(0, 137)}…`;
    return t;
  }

  function withWidthParam(src, w) {
    const s = String(src || '');
    if (!s) return '';
    return s.includes('?') ? `${s}&width=${w}` : `${s}?width=${w}`;
  }

  /**
   * @param {HTMLElement} section
   * @param {Record<string, unknown>} product
   * @param {string} root
   */
  function applyProductToDom(section, product, root) {
    const v = firstAvailableVariant(product);
    if (!v || typeof v !== 'object') return;
    const handle = String(product.handle || '');
    const variant = /** @type {{ id?: number|string, price?: string, compare_at_price?: string|null }} */ (v);

    const variantId = variant.id;
    if (variantId == null) return;

    const title = String(product.title || '');
    const bodyHtml = String(product.body_html || '');
    const desc = stripHtmlToText(bodyHtml);

    const images = /** @type {{ src?: string }[]} */ (product.images || []);
    const imgSrc = images[0]?.src || '';

    const priceStr = formatEur(variant.price);
    const compareRaw = variant.compare_at_price;
    const compareStr = compareRaw ? formatEur(String(compareRaw)) : '';
    const priceNum = parseFloat(String(variant.price || '').replace(',', '.'));
    const compareNum = compareRaw ? parseFloat(String(compareRaw).replace(',', '.')) : 0;
    const showCompare = compareNum > priceNum && compareStr;

    const urlPath = productPath(root, handle);
    const image200 = withWidthParam(imgSrc, 200);

    const img = section.querySelector('.js-blog-upsell-img');
    if (img instanceof HTMLImageElement && imgSrc) {
      img.src = withWidthParam(imgSrc, 180);
      img.alt = title;
      img.classList.remove('hidden');
    }

    const titleEl = section.querySelector('.js-blog-upsell-title');
    if (titleEl) {
      titleEl.textContent = title;
      if (titleEl instanceof HTMLAnchorElement) {
        titleEl.href = urlPath;
      }
    }

    const descEl = section.querySelector('.js-blog-upsell-desc');
    if (descEl) {
      if (desc) {
        descEl.textContent = desc;
        descEl.classList.remove('hidden');
      } else {
        descEl.textContent = '';
        descEl.classList.add('hidden');
      }
    }

    const priceEl = section.querySelector('.js-blog-upsell-price');
    if (priceEl) priceEl.textContent = priceStr;

    const compareEl = section.querySelector('.js-blog-upsell-compare');
    if (compareEl) {
      if (showCompare) {
        compareEl.textContent = compareStr;
        compareEl.classList.remove('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hidden');
      }
    }

    const form = section.querySelector('form[data-type="blog-upsell-form"]');
    if (form instanceof HTMLFormElement) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput instanceof HTMLInputElement) idInput.value = String(variantId);
      form.dataset.productId = String(product.id != null ? product.id : '');
      form.dataset.productTitle = title;
      form.dataset.productImage = image200;
      form.dataset.productUrl = urlPath;
    }
  }

  function hideLoading(section) {
    const el = section.querySelector('.js-blog-upsell-loading');
    if (el) el.classList.add('hidden');
  }

  /**
   * @param {HTMLElement} section
   */
  async function initBlogUpsellFromJson(section) {
    if (section.dataset.blogUpsellJsonDone === '1') return;
    section.dataset.blogUpsellJsonDone = '1';

    const root = section.getAttribute('data-root-url') || '';
    const collectionHandle = section.getAttribute('data-collection-handle') || 'all';
    const rawTitleKw = section.getAttribute('data-keywords') || '';
    const rawBodyKw = section.getAttribute('data-body-keywords') || '';
    const currentId = section.getAttribute('data-current-product-id') || '';

    const titleKeywords = rawTitleKw
      .split('|')
      .map((s) => s.trim().toLowerCase())
      .filter((k) => k.length >= 3 && !GENERIC_KW.has(k));

    const bodyKeywords = rawBodyKw
      .split('|')
      .map((s) => s.trim().toLowerCase())
      .filter((k) => k.length >= 3 && !GENERIC_KW.has(k));

    try {
      const products = await loadCatalogForKeywords(root, collectionHandle, titleKeywords, bodyKeywords);
      const picked = pickBestProduct(products, titleKeywords, bodyKeywords);
      if (!picked || String(picked.id || '') === currentId) return;

      applyProductToDom(section, picked, root);
    } catch (e) {
      console.warn('[blog-upsell]', e);
    } finally {
      hideLoading(section);
    }
  }

  function boot() {
    document.querySelectorAll('[data-blog-upsell-json="1"]').forEach((el) => {
      if (el instanceof HTMLElement) void initBlogUpsellFromJson(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
